import { StepType } from 'kb-shared/booking/types/types';

import { createURLSearchParams } from './urlUtils';

export enum RoutesPath {
  CHANGE_EMAIL = '/change-email',
  ENTER_MEMBERSHIP_ACCESS_CODE = '/activate-membership/code',
  MEMBERSHIP_ACTIVATION_SUCCESS = '/activate-membership/success',
  ENTER_MEMBERSHIP_UNIQUE_ID = '/activate-membership/unique-id',
  VERIFY_MEMBERSHIP_ELIGIBILITY = '/activate-membership/verify-eligibility',
  CONFIRM_MEMBERSHIP_ACTIVATION = '/activate-membership/confirm',
  DOCUMENTS = '/documents',
  RESULTS = '/results',
  RESULTS_V2 = '/results-v2',
  CONSENTS = '/consents',
  DASHBOARD = '/',
  CREATE_ACCOUNT = '/create-account',
  SIGN_UP = '/sign-up',
  APPOINTMENTS = '/appointments',
  APPOINTMENTS_V2 = '/appointments-v2',
  BOOK = '/book',
  BOOKING_QUIZ = '/booking/quiz',
  BOOKING_BOOK = '/booking/book',
  ACTIVATE_MEMBERSHIP_WALMART_WIN_BID = '/activate-membership/walmart/win-or-bid',
  ACTIVATE_MEMBERSHIP_WALMART_CONFIRM_IDENTITY = '/activate-membership/walmart/confirm-your-identity',
  CONFIRMATION = '/confirmation',
  MY_JOURNEY = '/my-journey',
  LOGIN = '/login',
  LOGIN_SSO_MEDTRONIC = '/login-sso/azure-medtronic',
  LOGOUT = '/logout',
  FORGOT_PASSWORD = '/forgot-password',
  ACCESS_ACCOUNT = '/access-account',
  VERIFICATION_CODE = '/verification-code',
  MEDICATION_INSTRUCTIONS = '/medication-instructions',
  MEDICATION_INSTRUCTION = '/medication-instruction',
  MY_PROFILE = '/my-profile',
  ACTIVATE_KINDBODY_BENEFIT = '/activate-kindbody-benefit',
  KINDBODY_BENEFIT = '/kindbody-benefit',
  INVOICES = '/invoices',
  RESOURCES = '/resources',
  KINDBODY_360 = '/kindbody/360',
  CARE_TEAM = '/care-team',
  CYCLE_PLAN = '/cycle-plan',
  PATIENT_INTAKE = '/patient-intake',
  VIOS_PAYMENT = '/vios-payment',
  VIOS_PAYMENTS = '/vios-payments',
  VIRTUAL_MEETING = '/virtual-meeting',
  CONFIRM_NEW_EMAIL = '/confirm-new-email',
  MESSAGES = '/messages',
  DOCUMENTS_V2 = '/v2/documents',
  SOLERA_LANDING = '/solera',
  APPOINTMENT_CONFIRMATION = '/confirm-appointment',
  MAINTENANCE = '/maintenance'
}

export const pageUrl = {
  myProfile: (
    tabId?:
      | 'demographic'
      | 'contact'
      | 'partner-invitations'
      | 'insurance'
      | 'employer'
      | 'trustees'
      | 'care-team'
      | 'payment-options'
  ) => {
    if (tabId) {
      return `${RoutesPath.MY_PROFILE}?tab=${tabId}`;
    }

    return RoutesPath.MY_PROFILE;
  },
  booking: {
    index(parameters?: { step?: StepType; appointmentId?: string; timeSlotId?: string }) {
      const route = RoutesPath.BOOKING_BOOK;

      if (!parameters) {
        return route;
      }

      const { step, appointmentId, timeSlotId } = parameters;

      const searchParameters = createURLSearchParams({
        step,
        appointment_id: appointmentId,
        time_slot_id: timeSlotId
      });

      return `${route}?${searchParameters}`;
    },
    bookingQuiz(params?: { type: 'question' | 'answer'; id: string | number }) {
      if (params?.type === 'question') {
        return `${RoutesPath.BOOKING_QUIZ}?q=${params.id}`;
      }

      if (params?.type === 'answer') {
        return `${RoutesPath.BOOKING_QUIZ}?a=${params.id}`;
      }

      return RoutesPath.BOOKING_QUIZ;
    },
    confirmation(): string {
      return RoutesPath.CONFIRMATION;
    }
  },

  messages: (urlParameters?: MessageUrlParameters) => {
    const categoryName = urlParameters?.categoryName;
    const conversationId = urlParameters?.conversationId;
    const subject = urlParameters?.subject;
    const message = urlParameters?.message;

    if (categoryName && conversationId) {
      return `${RoutesPath.MESSAGES}/${categoryName}/${conversationId}`;
    }

    if (categoryName && subject && message) {
      return `${RoutesPath.MESSAGES}/${categoryName}?subject=${encodeURIComponent(
        subject
      )}&message=${encodeURIComponent(message)}`;
    }

    if (categoryName && subject) {
      return `${RoutesPath.MESSAGES}/${categoryName}?subject=${encodeURIComponent(subject)}`;
    }

    if (categoryName) {
      return `${RoutesPath.MESSAGES}/${categoryName}`;
    }

    return RoutesPath.MESSAGES;
  },
  createAccount: (parameters?: {
    step?: StepType | 'create_account';
    previous?: string;
    appointmentId?: number | string;
    timeSlotId?: number | string;
    target?: string;
    soleraUid?: string;
  }) => {
    const route = RoutesPath.CREATE_ACCOUNT;

    if (!parameters) return route;

    const { step, previous, appointmentId, timeSlotId, target, soleraUid } = parameters;

    const searchParameters = createURLSearchParams({
      step,
      previous,
      appointment_id: appointmentId,
      time_slot_id: timeSlotId,
      target,
      solera_uid: soleraUid || undefined
    });

    return `${route}?${searchParameters}`;
  },
  intakeForm: (): string => {
    return RoutesPath.PATIENT_INTAKE;
  },
  login: (parameters?: {
    target?: string;
    membership_id?: string;
    enterprise_membership?: string;
  }) => {
    const route = RoutesPath.LOGIN;

    if (!parameters) return route;

    const { target, membership_id, enterprise_membership } = parameters;

    const searchParameters = createURLSearchParams({
      target,
      membership_id,
      enterprise_membership
    });

    return `${route}?${searchParameters}`;
  },
  loginSsoMedtronic: (): string => {
    return RoutesPath.LOGIN_SSO_MEDTRONIC;
  },
  emailAddressVerification: (): string => {
    return RoutesPath.VERIFICATION_CODE;
  },
  passwordReset: (parameters?: { email?: string }): string => {
    const route = RoutesPath.FORGOT_PASSWORD;

    if (!parameters) return route;

    const { email } = parameters;

    return `${route}?${createURLSearchParams({ email })}`;
  },
  enterpriseMembershipAccessCode: (): string => {
    return RoutesPath.ENTER_MEMBERSHIP_ACCESS_CODE;
  },
  documents: (): string => {
    return RoutesPath.DOCUMENTS;
  },
  results: (): string => {
    return RoutesPath.RESULTS;
  },
  resultsV2: (): string => {
    return RoutesPath.RESULTS_V2;
  },
  consents: (id?: string | number): string => {
    if (id) {
      return `${RoutesPath.CONSENTS}/${id}`;
    }

    return RoutesPath.CONSENTS;
  },
  logout: (parameters?: { maintenance?: boolean; deactivatedAccess?: boolean }): string => {
    const route = RoutesPath.LOGOUT;

    if (!parameters) return route;

    const { maintenance, deactivatedAccess } = parameters;

    return `${route}?${createURLSearchParams({
      maintenance,
      deactivated_access: deactivatedAccess
    })}`;
  },
  invoices: (urlParameters?: InvoiceUrlParameters) => {
    if (urlParameters?.tabId) return `${RoutesPath.INVOICES}?tab=${urlParameters.tabId}`;

    if (urlParameters?.invoiceIdentifier) {
      return `${RoutesPath.INVOICES}/${urlParameters.invoiceIdentifier}`;
    }

    return RoutesPath.INVOICES;
  },
  dashboard: (): string => {
    return RoutesPath.DASHBOARD;
  },
  myJourney: (): string => {
    return RoutesPath.MY_JOURNEY;
  },
  appointmentsV2: (): string => {
    return RoutesPath.APPOINTMENTS_V2;
  },
  membershipActivationSuccess: (): string => {
    return RoutesPath.MEMBERSHIP_ACTIVATION_SUCCESS;
  },
  cyclePlanAcknowledge: (): string => {
    return `${RoutesPath.CYCLE_PLAN}/:id/acknowledge`;
  },
  medicationInstructions: (): string => {
    return RoutesPath.MEDICATION_INSTRUCTIONS;
  },
  medicationInstruction: (): string => {
    return RoutesPath.MEDICATION_INSTRUCTION;
  },
  activateKindbodyBenefit: (): string => {
    return RoutesPath.ACTIVATE_KINDBODY_BENEFIT;
  },
  kindbodyBenefit: (): string => {
    return RoutesPath.KINDBODY_BENEFIT;
  },
  viosPayments: (): string => {
    return RoutesPath.VIOS_PAYMENTS;
  },
  viosPayment: (): string => {
    return RoutesPath.VIOS_PAYMENT;
  },
  signUp: (): string => {
    return RoutesPath.SIGN_UP;
  },
  accessAccount: (): string => {
    return RoutesPath.ACCESS_ACCOUNT;
  },
  changeEmail: (): string => {
    return RoutesPath.CHANGE_EMAIL;
  },
  confirmNewEmail: (): string => {
    return RoutesPath.CONFIRM_NEW_EMAIL;
  },
  virtualMeeting: (): string => {
    return RoutesPath.VIRTUAL_MEETING;
  },
  appointmentVirtualMeeting: (): string => {
    return `${RoutesPath.VIRTUAL_MEETING}/:appointmentPublicIdentifier`;
  },
  maintenance: (): string => {
    return RoutesPath.MAINTENANCE;
  }
};

export interface MessageUrlParameters {
  categoryName?: string;
  conversationId?: string;
  subject?: string;
  message?: string;
}

export interface InvoiceUrlParameters {
  tabId?: 'unapplied-payments' | 'payments' | 'invoices';
  invoiceIdentifier?: string;
}
